import { useEffect, useState } from "react";
import { getWorkflowInProgressCount } from "../services/workflowService";

/**
 * The epic: the badge refreshes on navigation and polls about every 60 seconds - only while the
 * tab is visible, refreshing once when a hidden tab comes back (PR !32534 review).
 */
export const WORKFLOW_COUNT_REFRESH_MS = 60000;

/**
 * Epic 80431 - the sidebar "Instances & My Work" badge: in-progress cases in the caller's scope
 * (test runs and finished cases never count - the server leaves them out).
 * <p>
 * Deliberately NOT through useApi: a background poll that fails should keep the last count
 * quietly, not raise an alert every minute. Pass `enabled = false` for a user without Instances
 * access - the call would only 403.
 */
const useWorkflowInProgressCount = (enabled: boolean, refreshKey: string) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }
    let active = true;
    const refresh = () => {
      getWorkflowInProgressCount()
        .then((total) => {
          if (active) setCount(total);
        })
        .catch(() => {
          // Keep the last count; the next poll or navigation tries again.
        });
    };
    // A hidden tab skips its polls - every open tab would otherwise poll all day.
    const refreshIfVisible = () => {
      if (!document.hidden) refresh();
    };
    refresh();
    const interval = window.setInterval(refreshIfVisible, WORKFLOW_COUNT_REFRESH_MS);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [enabled, refreshKey]);

  return count;
};

export default useWorkflowInProgressCount;
